import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

import styles from './Field.module.css';

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> & {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  prefix?: ReactNode;
};

/** Labelled text input; error and hint are tied to the input with aria-describedby. */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, prefix, id, required, disabled, className, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const messageId = `${inputId}-message`;
  const message = error || hint;
  return (
    <div className={`${styles.field} ${className ?? ''}`}>
      <label htmlFor={inputId} className={styles.label}>
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </label>
      <span
        className={[styles.control, error ? styles.invalid : '', disabled ? styles.disabled : '']
          .filter(Boolean)
          .join(' ')}
      >
        {prefix && <span className={styles.prefix}>{prefix}</span>}
        <input
          {...rest}
          ref={ref}
          id={inputId}
          required={required}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={message ? messageId : undefined}
          className={styles.input}
        />
      </span>
      {message && (
        <span id={messageId} className={error ? styles.error : styles.hint}>
          {message}
        </span>
      )}
    </div>
  );
});
